import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { obterSocket } from "../../lib/socket.js";
import { useAuth } from "../auth/useAuth.js";
import { Botao, Campo, Chave, Entrada, Falha, Girando, Selecao, cx } from "../../components/ui.js";
import { FILA_VAZIA, avancar, sincronizar } from "./fila.js";
import type { EstadoFila, Slide } from "./fila.js";
import { useTelaCheia } from "./use-tela-cheia.js";
import { gravarPreferencias, lerPreferencias } from "./preferencias.js";
import type { PreferenciasGaleria } from "./preferencias.js";
import { lerLinkDaGaleria } from "./link.js";
import { EscolhaDeMusicaNaGaleria } from "./EscolhaDeMusica.js";
import { TrilhaSonora, type ControleDaTrilha } from "./TrilhaSonora.js";

/**
 * A galeria do salão.
 *
 * Roda numa TV, sem login, sem ninguém por perto para consertar. Tudo aqui é
 * governado por isso: recupera sozinha de falha de rede, nunca mostra tela de
 * erro, e uma foto recém-aprovada fura a fila para aparecer enquanto a pessoa
 * ainda está na sala.
 */
export function GaleriaPage() {
  // O link que põe a TV no ar (`/kiosk` → "Copiar link"), lido uma vez.
  const [link] = useState(() => lerLinkDaGaleria(location.hash));

  const [vivenciaId, setVivenciaId] = useState<string | null>(
    () => link?.vivenciaId ?? new URLSearchParams(location.search).get("vivencia"),
  );
  const [rodando, setRodando] = useState(false);
  const telaCheia = useTelaCheia();

  const [prefs, setPrefs] = useState<PreferenciasGaleria>(
    () => link?.preferencias ?? lerPreferencias(),
  );
  useEffect(() => gravarPreferencias(prefs), [prefs]);
  const mudar = (parcial: Partial<PreferenciasGaleria>) =>
    setPrefs((atual) => ({ ...atual, ...parcial }));

  const { autenticado, carregando: carregandoSessao, recarregar, papel, usuario } = useAuth();

  // As consultas da galeria exigem sessão: sem ela, a TV veria erro em vez de
  // pedir o código.
  const vivencias = trpc.vivencias.listActive.useQuery(undefined, { enabled: autenticado });
  const playlistsDaTv = trpc.playlists.daGaleria.useQuery(undefined, { enabled: autenticado });
  const musicasDaTv = trpc.musicas.daGaleria.useQuery(undefined, { enabled: autenticado });
  const config = trpc.config.getPublic.useQuery();

  // A TV do suporte escolhe de quem projetar: o próprio admin ou um cliente. A
  // escolha troca o dono da sessão no servidor, então tudo que a galeria consulta
  // precisa vir de novo — e a vivência escolhida antes era do dono anterior.
  const utils = trpc.useUtils();
  const tv = trpc.kiosk.daTv.useQuery(undefined, { enabled: autenticado });
  const [erroDono, setErroDono] = useState<string | null>(null);
  const projetar = trpc.kiosk.projetarPara.useMutation({
    onSuccess: () => {
      setVivenciaId(null);
      void utils.invalidate();
    },
    onError: (e) => setErroDono(e.message),
  });
  const suporte = tv.data?.escolheCliente ? tv.data : null;
  const semEscolha = suporte !== null && suporte.donoId === null;

  /**
   * Entrar pelo link, sem ninguém digitar.
   *
   * Vale também quando a sessão vence com a projeção no ar (ela dura 12 h, e um
   * evento pode durar dias): a TV cai de novo aqui e entra sozinha. Código que
   * não vale mais — alguém gerou outro — para na tela do código, com o aviso;
   * falha de rede tenta de novo em meio minuto, porque ninguém vai estar lá
   * para apertar nada.
   */
  const [linkInvalido, setLinkInvalido] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  const tentouEntrar = useRef(false);
  const entrarPeloLink = trpc.auth.entrarNaGaleria.useMutation({
    onSuccess: () => {
      // A sessão é outra: o que se consultou antes (o dono da TV do suporte,
      // inclusive) era da anterior.
      void utils.invalidate();
      void recarregar();
    },
    onError: (e) => {
      if (e.data?.code === "UNAUTHORIZED") {
        setLinkInvalido(true);
        return;
      }
      setTimeout(() => {
        tentouEntrar.current = false;
        setTentativa((t) => t + 1);
      }, 30_000);
    },
  });

  useEffect(() => {
    if (autenticado) tentouEntrar.current = false;
  }, [autenticado]);

  useEffect(() => {
    if (!link || linkInvalido || carregandoSessao || autenticado || tentouEntrar.current) return;
    tentouEntrar.current = true;
    entrarPeloLink.mutate({ codigo: link.codigo });
  }, [link, linkInvalido, carregandoSessao, autenticado, tentativa, entrarPeloLink]);

  /**
   * Aplicar o link, uma vez por sessão, e começar.
   *
   * Na TV do suporte, o cliente vem primeiro — trocar o dono zera a vivência —,
   * e só então a vivência e o início. Sem tela cheia: o navegador só a concede
   * num toque, e o primeiro toque na projeção a pede.
   */
  const [linkAplicado, setLinkAplicado] = useState(false);
  const aplicou = useRef(false);
  useEffect(() => {
    if (!autenticado) aplicou.current = false;
  }, [autenticado]);

  useEffect(() => {
    if (!link || !autenticado || papel !== "kiosk" || aplicou.current) return;
    if (!tv.data || tv.isFetching) return;
    aplicou.current = true;

    setPrefs(link.preferencias);
    const comecar = () => {
      setVivenciaId(link.vivenciaId);
      setLinkAplicado(true);
      setRodando(true);
    };

    if (tv.data.escolheCliente && link.donoId !== null && tv.data.donoId !== link.donoId) {
      projetar.mutate({ donoId: link.donoId }, { onSuccess: comecar });
    } else {
      comecar();
    }
  }, [link, autenticado, papel, tv.data, tv.isFetching, projetar]);

  // Link aberto num navegador de quem usa o sistema: entrar com o código
  // trocaria a sessão da pessoa pela da TV. Só com a escolha dela.
  const [ignorarLink, setIgnorarLink] = useState(false);

  if (carregandoSessao) {
    return (
      <main className="flex min-h-full items-center justify-center">
        <Girando className="size-6 text-texto-fraco" />
      </main>
    );
  }

  if (!autenticado) {
    if (link && !linkInvalido) return <AbrindoPeloLink />;
    return (
      <EntrarComCodigo
        aoEntrar={recarregar}
        aviso={
          linkInvalido
            ? "Este link não vale mais: o código da TV foi trocado. Peça o link novo, ou digite o código."
            : null
        }
      />
    );
  }

  if (link && papel !== "kiosk" && !ignorarLink) {
    return (
      <LinkDeOutraConta
        nome={usuario?.name ?? ""}
        entrando={entrarPeloLink.isPending}
        aoEntrarComoTv={() => entrarPeloLink.mutate({ codigo: link.codigo })}
        aoContinuar={() => setIgnorarLink(true)}
      />
    );
  }

  // Entre a sessão chegar e a configuração do link terminar de valer.
  if (link && papel === "kiosk" && !linkAplicado && !rodando) return <AbrindoPeloLink />;

  if (!rodando) {
    return (
      <main className="flex min-h-full items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-[1.75rem] leading-tight tracking-[-0.02em]">Galeria</h1>
          <p className="mt-2 text-[0.9375rem] leading-relaxed text-texto-suave">
            Iniciar abre em tela cheia. As fotos aprovadas entram sozinhas, e a ordem muda a cada
            volta.
          </p>

          {suporte && (
            <div className="mt-6">
              <Campo
                rotulo="Projetar"
                erro={erroDono}
                dica="As suas fotos, ou exatamente o que a TV de um cliente mostraria."
              >
                <Selecao
                  value={suporte.donoId ?? ""}
                  disabled={projetar.isPending}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    setErroDono(null);
                    projetar.mutate({ donoId: Number(e.target.value) });
                  }}
                >
                  <option value="" disabled>
                    Selecione
                  </option>
                  {suporte.opcoes.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </Selecao>
              </Campo>
            </div>
          )}

          <label className={cx("block", suporte ? "mt-4" : "mt-6")}>
            <span className="mb-1.5 block text-[0.8125rem] font-medium text-texto-suave">
              Mostrar fotos de
            </span>
            <Selecao
              value={vivenciaId ?? ""}
              disabled={semEscolha}
              onChange={(e) => setVivenciaId(e.target.value || null)}
            >
              <option value="">Todas as vivências</option>
              {vivencias.data?.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </Selecao>
          </label>

          {/* Dizer o que vai passar em cada modo, porque a diferença não é
              óbvia: numa vivência, as divulgações escolhidas para ela e na ordem
              definida; em todas, tudo que está ativo, inclusive o que ainda não
              foi vinculado a evento nenhum. */}
          <p className="mt-3 text-[0.8125rem] leading-relaxed text-texto-fraco">
            {semEscolha
              ? "Escolha de quem projetar para ver as vivências."
              : vivenciaId === null
                ? "Passam as fotos de todas as vivências e todas as divulgações ativas."
                : "Passam as fotos desta vivência e as divulgações escolhidas para ela, na ordem definida."}
          </p>

          {/* As escolhas valem para este aparelho e ficam gravadas nele: a TV
              do salão é ligada e desligada todo dia. */}
          <div className="mt-6 flex flex-col gap-3 border-t border-borda pt-5">
            <Opcao rotulo="Nome do espectador" dica="Legenda sobre a foto">
              <Chave
                ligada={prefs.mostrarNome}
                aoAlternar={(v) => mudar({ mostrarNome: v })}
                rotulo="Mostrar o nome do espectador"
              />
            </Opcao>

            <Opcao rotulo="Divulgações" dica="Peças do acervo entre as fotos">
              <Chave
                ligada={prefs.divulgacoes}
                aoAlternar={(v) => mudar({ divulgacoes: v })}
                rotulo="Intercalar divulgações"
              />
            </Opcao>

            {prefs.divulgacoes && (
              <Opcao rotulo="A cada" dica="Fotos entre uma divulgação e outra">
                {/* A largura vive no invólucro: `Entrada` é `w-full`. */}
                <div className="w-20 shrink-0">
                  <Entrada
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={50}
                    className="px-2 py-2 text-center"
                    placeholder={String(config.data?.galeriaDivulgacaoEvery ?? 5)}
                    value={prefs.intervalo ?? ""}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      mudar({ intervalo: n >= 1 && n <= 50 ? Math.floor(n) : null });
                    }}
                  />
                </div>
              </Opcao>
            )}

            <Opcao rotulo="Áudio" dica="Som nas divulgações em vídeo">
              <Chave
                ligada={prefs.audio}
                aoAlternar={(v) => mudar({ audio: v })}
                rotulo="Som nas divulgações em vídeo"
              />
            </Opcao>
          </div>

          <div className="mt-6 border-t border-borda pt-6">
            <EscolhaDeMusicaNaGaleria
              playlists={playlistsDaTv.data ?? []}
              musicas={musicasDaTv.data ?? []}
              valor={prefs}
              aoMudar={mudar}
            />
          </div>

          <Botao
            tom="brasa"
            largo
            className="mt-6"
            // Sem escolha, a TV do suporte não tem de quem projetar.
            disabled={semEscolha || projetar.isPending}
            onClick={() => {
              // Dentro do clique, que é o gesto que o navegador exige.
              void telaCheia.entrar();
              setRodando(true);
            }}
          >
            Iniciar
          </Botao>

          <SairDaGaleria />
        </div>
      </main>
    );
  }

  return (
    <Projecao
      vivenciaId={vivenciaId}
      aoSair={() => {
        void telaCheia.sair();
        setRodando(false);
      }}
      // Para a TV que abriu por link e não passou pelo botão: um toque na
      // projeção entra em tela cheia. Não atrapalha o duplo clique de saída —
      // entrar duas vezes é inócuo.
      aoTocar={() => void telaCheia.entrar()}
      preferencias={prefs}
    />
  );
}

function Opcao({
  rotulo,
  dica,
  children,
}: {
  rotulo: string;
  dica: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[0.875rem] text-texto">{rotulo}</p>
        <p className="text-[0.75rem] text-texto-fraco">{dica}</p>
      </div>
      {children}
    </div>
  );
}

/**
 * A entrada da TV.
 *
 * Código de seis caracteres em vez de e-mail e senha: quem monta a projeção
 * está com um controle remoto na mão, e digitar credencial ali é hostil. O
 * código é gerado por cliente e re-gerável — a conta de quiosque não atende,
 * não apaga e não envia nada, então ele é chave de leitura, não senha.
 */
/** Enquanto a TV entra e aplica o link: nada para ninguém fazer. */
function AbrindoPeloLink() {
  return (
    <main className="flex min-h-full flex-col items-center justify-center gap-4 px-6">
      <Girando className="size-6 text-texto-fraco" />
      <p className="text-[0.9375rem] text-texto-suave">Abrindo a galeria…</p>
    </main>
  );
}

/**
 * O link da TV aberto por quem está logado no sistema.
 *
 * Entrar com o código regeneraria a sessão: a pessoa sairia da própria conta
 * sem entender por quê. Acontece com quem testa o link que acabou de copiar.
 */
function LinkDeOutraConta({
  nome,
  entrando,
  aoEntrarComoTv,
  aoContinuar,
}: {
  nome: string;
  entrando: boolean;
  aoEntrarComoTv: () => void;
  aoContinuar: () => void;
}) {
  return (
    <main className="flex min-h-full items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm">
        <h1 className="font-display text-[1.75rem] leading-tight tracking-[-0.02em]">
          Este link é para a TV
        </h1>
        <p className="mt-2 text-[0.9375rem] leading-relaxed text-texto-suave">
          Você está conectado{nome ? ` como ${nome}` : ""}. Entrar como a TV encerra a sua sessão
          neste navegador — para testar o link sem sair, abra-o numa janela anônima.
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <Botao tom="brasa" largo carregando={entrando} onClick={aoEntrarComoTv}>
            Entrar como a TV
          </Botao>
          <Botao tom="fantasma" largo onClick={aoContinuar}>
            Continuar na minha conta
          </Botao>
        </div>
      </div>
    </main>
  );
}

function EntrarComCodigo({
  aoEntrar,
  aviso = null,
}: {
  aoEntrar: () => void;
  /** Por que a TV caiu aqui em vez de entrar sozinha. */
  aviso?: string | null;
}) {
  const [codigo, setCodigo] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const entrar = trpc.auth.entrarNaGaleria.useMutation({
    onSuccess: aoEntrar,
    onError: (e) => setErro(e.message),
  });

  return (
    <main className="flex min-h-full items-center justify-center px-6 py-12">
      <form
        className="w-full max-w-sm"
        onSubmit={(e) => {
          e.preventDefault();
          setErro(null);
          entrar.mutate({ codigo });
        }}
      >
        <h1 className="font-display text-[1.75rem] leading-tight tracking-[-0.02em]">Galeria</h1>
        <p className="mt-2 text-[0.9375rem] leading-relaxed text-texto-suave">
          Digite o código do quiosque. Ele está em Kiosk, no painel de quem contratou.
        </p>

        {aviso && (
          <div className="mt-4">
            <Falha mensagem={aviso} />
          </div>
        )}

        <div className="mt-6">
          <Campo rotulo="Código">
            <Entrada
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              autoFocus
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              maxLength={12}
              className="text-center font-mono text-[1.5rem] tracking-[0.3em]"
            />
          </Campo>
        </div>

        {erro && (
          <div className="mt-4">
            <Falha mensagem={erro} />
          </div>
        )}

        <Botao
          tom="brasa"
          className="mt-6 w-full"
          disabled={codigo.trim().length < 4}
          carregando={entrar.isPending}
        >
          Entrar
        </Botao>
      </form>
    </main>
  );
}

/**
 * Saída da conta de quiosque.
 *
 * É a única tela que ela alcança: sem isto, ficaria presa até alguém limpar o
 * cookie do aparelho.
 */
function SairDaGaleria() {
  const { usuario } = useAuth();
  const sair = trpc.auth.logout.useMutation({
    onSettled: () => window.location.assign("/"),
  });

  if (!usuario) return null;

  return (
    <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-borda pt-4">
      <div className="min-w-0">
        <p className="truncate text-[0.8125rem] text-texto-suave">{usuario.name}</p>
        <p className="truncate font-mono text-[0.75rem] text-texto-fraco">{usuario.email}</p>
      </div>
      <Botao tom="quieto" onClick={() => sair.mutate()} carregando={sair.isPending}>
        Sair
      </Botao>
    </div>
  );
}

/**
 * Teto de segurança para vídeo, em segundos. Só entra em ação quando o `ended`
 * não vem — arquivo corrompido, codec que o navegador não decodifica.
 */
const TETO_VIDEO_SEGUNDOS = 600;

/**
 * Duração do crossfade.
 *
 * Vale para o CSS e para o temporizador que desmonta a peça de baixo: os dois
 * leem daqui, porque se divergirem sobra um quadro preto no fim (CSS mais
 * curto) ou a peça antiga fica parada na tela (CSS mais longo).
 */
const CROSSFADE_MS = 700;

function Projecao({
  vivenciaId,
  aoSair,
  aoTocar,
  preferencias,
}: {
  vivenciaId: string | null;
  aoSair: () => void;
  aoTocar: () => void;
  preferencias: PreferenciasGaleria;
}) {
  const [estado, setEstado] = useState<EstadoFila>(FILA_VAZIA);
  const [prioritario, setPrioritario] = useState<Slide | null>(null);
  const trilha = useRef<ControleDaTrilha | null>(null);
  const vistos = useRef(new Set<number>());
  const utils = trpc.useUtils();

  const config = trpc.config.getPublic.useQuery();

  const fotos = trpc.generations.listApprovedMedia.useQuery(
    { vivenciaId: vivenciaId ?? undefined, limit: 200 },
    {
      // Rede de salão cai. O polling é a rede de segurança: mesmo sem socket,
      // a galeria continua se atualizando.
      refetchInterval: 20_000,
      refetchOnWindowFocus: true,
    },
  );

  // Duas listagens porque a pergunta é outra em cada modo: numa vivência, o que
  // foi escolhido para ela e na ordem definida; em "todas", tudo que está ativo.
  const daVivencia = trpc.divulgacoes.listByVivencia.useQuery(
    { vivenciaId: vivenciaId! },
    { enabled: vivenciaId !== null && preferencias.divulgacoes, refetchInterval: 120_000 },
  );
  const todasAtivas = trpc.divulgacoes.listAtivas.useQuery(undefined, {
    enabled: vivenciaId === null && preferencias.divulgacoes,
    refetchInterval: 120_000,
  });
  const divulgacoes = vivenciaId === null ? todasAtivas : daVivencia;

  const segundosFoto = config.data?.galeriaPhotoDisplaySeconds ?? 8;
  const aCada = preferencias.intervalo ?? config.data?.galeriaDivulgacaoEvery ?? 5;

  const fotosSlides = useMemo<Slide[]>(
    () =>
      (fotos.data ?? []).map((g) => ({
        chave: `f${g.id}`,
        url: g.url,
        urlOriginal: g.urlOriginal,
        legenda: g.spectatorName,
        tipo: "image",
        segundos: segundosFoto,
      })),
    [fotos.data, segundosFoto],
  );

  const divulgacoesSlides = useMemo<Slide[]>(
    () =>
      preferencias.divulgacoes
        ? (divulgacoes.data ?? []).map((x) => ({
            chave: `d${x.id}`,
            url: x.url,
            urlOriginal: x.urlOriginal,
            tipo: x.mediaType,
            segundos: x.durationSeconds,
          }))
        : [],
    [divulgacoes.data, preferencias.divulgacoes],
  );

  /**
   * As fontes ficam num ref para que `avancarSlide` não precise ser recriada a
   * cada polling — ela é dependência do cronômetro, e uma identidade nova a
   * cada 20 s reiniciaria a contagem do slide em exibição.
   */
  const fontes = useRef({ fotos: fotosSlides, divulgacoes: divulgacoesSlides, aCada });

  useEffect(() => {
    fontes.current = { fotos: fotosSlides, divulgacoes: divulgacoesSlides, aCada };
    setEstado((e) => sincronizar(e, fotosSlides, divulgacoesSlides, aCada));
  }, [fotosSlides, divulgacoesSlides, aCada]);

  // Uma foto recém-aprovada fura a fila: a pessoa ainda está na sala, e ver a
  // própria foto entrar é o momento que o sistema inteiro existe para produzir.
  useEffect(() => {
    const socket = obterSocket();

    const aoAtualizar = (e: { generationId: number; imageApproved: boolean }) => {
      if (!e.imageApproved || vistos.current.has(e.generationId)) return;
      vistos.current.add(e.generationId);
      void utils.generations.listApprovedMedia.invalidate();
    };

    socket.on("generation:updated", aoAtualizar);
    return () => void socket.off("generation:updated", aoAtualizar);
  }, [utils]);

  // Marca como vistas as que já estavam na lista, para não interromper por
  // fotos antigas ao abrir a galeria.
  useEffect(() => {
    const atual = fotos.data ?? [];
    if (vistos.current.size === 0 && atual.length > 0) {
      atual.forEach((g) => vistos.current.add(g.id));
      return;
    }

    const nova = atual.find((g) => !vistos.current.has(g.id));
    if (!nova) return;

    atual.forEach((g) => vistos.current.add(g.id));
    setPrioritario({
      chave: `f${nova.id}`,
      url: nova.url,
      legenda: nova.spectatorName,
      tipo: "image",
      segundos: segundosFoto + 4,
    });
  }, [fotos.data, segundosFoto]);

  const slide = prioritario ?? estado.fila[estado.indice];

  // O ref existe porque `avancarSlide` é chamada de timer e de evento de mídia,
  // fora do render: precisa saber se há prioritário na tela sem depender dele.
  const prioritarioRef = useRef<Slide | null>(null);
  useEffect(() => {
    prioritarioRef.current = prioritario;
  }, [prioritario]);

  const avancarSlide = useCallback(() => {
    if (prioritarioRef.current) {
      setPrioritario(null);
      return;
    }
    const f = fontes.current;
    setEstado((e) => avancar(e, f.fotos, f.divulgacoes, f.aCada));
  }, []);

  /**
   * Avanço por falha de carregamento, com freio.
   *
   * Sem a espera, um lote inteiro de URLs vencidas faria a fila girar na
   * velocidade do render — CPU a 100% numa TV que ninguém está olhando. Com
   * ela, a galeria percorre a fila devagar até uma imagem carregar.
   */
  const falha = useRef<ReturnType<typeof setTimeout> | null>(null);
  const avancarPorFalha = useCallback(() => {
    if (falha.current) return;
    falha.current = setTimeout(() => {
      falha.current = null;
      avancarSlide();
    }, 1200);
  }, [avancarSlide]);

  /**
   * Quanto tempo este slide fica na tela.
   *
   * Para imagem, é o tempo configurado. Para vídeo, o avanço é o `onEnded`, e
   * este prazo passa a ser rede de segurança: um arquivo corrompido que nunca
   * dispara `ended` congelaria a TV para sempre. O teto é generoso de propósito
   * — cortar um vídeo bom é pior do que esperar um ruim.
   */
  const prazo = slide
    ? slide.tipo === "video"
      ? Math.max(slide.segundos, TETO_VIDEO_SEGUNDOS) * 1000
      : slide.segundos * 1000
    : 0;

  // `estado.passo` entra nas dependências porque com um item só o slide é
  // sempre a mesma referência: sem ele, o cronômetro não reiniciava e a
  // projeção congelava — com vídeo, no último quadro.
  useEffect(() => {
    if (!slide) return;

    const t = setTimeout(avancarSlide, prazo);
    return () => {
      clearTimeout(t);
      if (falha.current) {
        clearTimeout(falha.current);
        falha.current = null;
      }
    };
  }, [slide, estado.passo, prazo, avancarSlide]);

  /** Identidade do que está na tela agora. Muda a cada avanço, e o elemento remonta. */
  const idDoSlide = prioritario ? `p${prioritario.chave}` : `${slide?.chave}#${estado.passo}`;

  /**
   * O crossfade.
   *
   * A peça que sai continua montada embaixo enquanto a nova aparece por cima —
   * sem isso a tela passa pelo preto a cada troca, que é o que incomodava.
   *
   * Só imagem fica embaixo. Congelar o último quadro de um vídeo exigiria
   * copiá-lo para um canvas antes da troca; remontar o `<video>` mostraria o
   * primeiro quadro, que é pior do que não ter transição.
   */
  const [saindo, setSaindo] = useState<{ id: string; url: string } | null>(null);
  const [carregado, setCarregado] = useState<string | null>(null);
  const naTela = useRef<{ id: string; url: string; tipo: Slide["tipo"] } | null>(null);

  useEffect(() => {
    if (!slide) return;

    const anterior = naTela.current;
    naTela.current = { id: idDoSlide, url: slide.url, tipo: slide.tipo };

    if (!anterior || anterior.id === idDoSlide) return;
    if (anterior.tipo !== "image") {
      setSaindo(null);
      return;
    }

    setSaindo({ id: anterior.id, url: anterior.url });
  }, [idDoSlide, slide]);

  /**
   * A peça de baixo só sai depois que a de cima **carregou** e completou o
   * crossfade.
   *
   * Contar a partir da montagem, e não do carregamento, era a piscada: a nova
   * imagem entra vazia, os 700 ms correm com ela ainda em branco, a de baixo é
   * removida, e só então os bytes chegam — a foto aparecia de estalo sobre o
   * preto. Enquanto a nova não vem, é a anterior que fica na tela, que é o pior
   * caso aceitável numa projeção.
   */
  useEffect(() => {
    if (!saindo || carregado !== idDoSlide) return;

    const t = setTimeout(() => setSaindo(null), CROSSFADE_MS);
    return () => clearTimeout(t);
  }, [saindo, carregado, idDoSlide]);

  /**
   * Puxa a próxima peça para o cache do navegador enquanto esta está na tela.
   *
   * Com oito segundos de folga, a troca seguinte encontra a imagem pronta e o
   * crossfade começa no mesmo quadro. O sistema anterior calculava o próximo
   * item e descartava o resultado — o pré-carregamento estava especificado e
   * nunca chegou a existir.
   */
  useEffect(() => {
    const proximo = estado.fila[(estado.indice + 1) % Math.max(estado.fila.length, 1)];
    if (!proximo || proximo.tipo !== "image") return;

    const img = new Image();
    img.src = proximo.url;
  }, [estado.fila, estado.indice]);

  // A TV não deve apagar no meio do evento.
  useEffect(() => {
    let sentinela: WakeLockSentinel | null = null;
    const pedir = async () => {
      try {
        sentinela = await navigator.wakeLock?.request("screen");
      } catch {
        // Sem wake lock o slideshow continua; a tela é que pode apagar.
      }
    };
    void pedir();

    const aoVoltar = () => document.visibilityState === "visible" && void pedir();
    document.addEventListener("visibilitychange", aoVoltar);

    return () => {
      document.removeEventListener("visibilitychange", aoVoltar);
      void sentinela?.release();
    };
  }, []);

  // O toque que pede tela cheia é o mesmo que liga a música: é o único gesto
  // que uma TV aberta pelo link vai receber.
  const aoTocarNaProjecao = () => {
    aoTocar();
    trilha.current?.desbloquear();
  };
  const musica = (
    <TrilhaSonora
      escolha={preferencias.musica}
      aleatoria={preferencias.musicaAleatoria}
      repetir={preferencias.musicaRepetir}
      pausada={slide?.tipo === "video" && preferencias.audio}
      controle={trilha}
    />
  );

  // A trilha fica fora do `<main>` e na mesma posição nas duas telas: assim
  // ela continua montada quando a primeira foto chega, e a música não
  // recomeça do zero.
  if (!slide) {
    return (
      <>
        {musica}
        <main
          className="flex min-h-full flex-col items-center justify-center gap-3 bg-black px-6 text-center"
          onClick={aoTocarNaProjecao}
        >
          <p className="font-display text-[1.5rem]">A galeria começa com a primeira foto</p>
          <p className="text-[0.9375rem] text-texto-suave">
            Assim que um atendimento for finalizado, ela aparece aqui.
          </p>
          <Botao tom="fantasma" className="mt-4" onClick={aoSair}>
            Voltar
          </Botao>
        </main>
      </>
    );
  }

  return (
    <>
      {musica}
      <main
        className="relative min-h-full bg-black"
        style={{ "--crossfade": `${CROSSFADE_MS}ms` } as React.CSSProperties}
        onClick={aoTocarNaProjecao}
        onDoubleClick={aoSair}
      >
        {/* A foto que está saindo. Fica embaixo, opaca, até a nova ter carregado —
          e então as duas animam juntas: esta apaga enquanto aquela aparece. */}
        {saindo && (
          <img
            key={saindo.id}
            src={saindo.url}
            alt=""
            aria-hidden
            className={cx(
              "absolute inset-0 size-full object-contain",
              carregado === idDoSlide &&
                "motion-safe:animate-[sumindo_var(--crossfade)_ease-in_forwards]",
            )}
          />
        )}

        {slide.tipo === "video" ? (
          <video
            // O passo na `key` remonta o elemento a cada avanço. É o que faz um
            // vídeo sozinho na fila repetir em vez de parar no último quadro.
            key={idDoSlide}
            src={slide.url}
            autoPlay
            // Mudo por padrão, e não só por gosto: com som o navegador pode
            // recusar o autoplay e a projeção pararia no primeiro vídeo.
            muted={!preferencias.audio}
            playsInline
            // Vídeo toca inteiro: quem manda no avanço é o fim do arquivo, não o
            // cronômetro. Antes um vídeo de 30s morria nos 8s do padrão.
            onEnded={avancarSlide}
            // Falha de carregamento não pode parar a projeção — segue para o
            // próximo em vez de deixar a tela preta até o teto de segurança.
            onError={avancarPorFalha}
            // O primeiro quadro decodificado é o sinal de que há o que mostrar.
            onLoadedData={() => setCarregado(idDoSlide)}
            className={cx(
              "absolute inset-0 size-full object-contain",
              carregado === idDoSlide
                ? "motion-safe:animate-[surgindo_var(--crossfade)_ease-out_1]"
                : "opacity-0",
            )}
          />
        ) : (
          <Foto
            key={idDoSlide}
            slide={slide}
            aoFalhar={avancarPorFalha}
            aoCarregar={() => setCarregado(idDoSlide)}
            pronta={carregado === idDoSlide}
          />
        )}

        {slide.legenda && preferencias.mostrarNome && (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-8 pb-10 pt-24">
            <p className="text-center font-display text-[clamp(1.75rem,4vw,3.5rem)] leading-none tracking-[-0.02em] text-texto">
              {slide.legenda}
            </p>
          </div>
        )}

        {prioritario && (
          <span className="absolute left-6 top-6 rounded-full bg-brasa px-3 py-1.5 text-[0.875rem] font-semibold text-fundo">
            Acabou de sair
          </span>
        )}
      </main>
    </>
  );
}

/**
 * A foto na projeção, com plano B.
 *
 * A galeria pede a derivada de exibição, que é 90% mais leve. Se ela não estiver
 * lá — acervo ainda sem backfill, upload que escapou da invariante — a foto cai
 * para o arquivo original em vez de sumir do ciclo. Só depois de as duas
 * falharem é que a projeção segue adiante: numa TV que roda sozinha no evento,
 * a foto que não aparece é a de alguém que está na sala esperando se ver.
 *
 * O componente é remontado a cada passo (a `key` de quem o usa inclui o
 * contador), então o estado nasce limpo em cada slide.
 */
function Foto({
  slide,
  aoFalhar,
  aoCarregar,
  pronta,
}: {
  slide: Slide;
  aoFalhar: () => void;
  aoCarregar: () => void;
  /** Já carregou: pode aparecer. Antes disso fica invisível sobre a peça anterior. */
  pronta: boolean;
}) {
  const [url, setUrl] = useState(slide.url);
  const jaTentouOriginal = useRef(false);

  return (
    <img
      // Imagem vinda do cache pode completar antes de o React ligar o `onLoad`;
      // o ref confere o estado real do elemento e cobre esse caso.
      ref={(el) => {
        if (el?.complete && el.naturalWidth > 0) aoCarregar();
      }}
      src={url}
      alt={slide.legenda ?? ""}
      onLoad={aoCarregar}
      onError={() => {
        if (!jaTentouOriginal.current && slide.urlOriginal && slide.urlOriginal !== url) {
          jaTentouOriginal.current = true;
          setUrl(slide.urlOriginal);
          return;
        }
        aoFalhar();
      }}
      className={cx(
        "absolute inset-0 size-full object-contain",
        pronta ? "motion-safe:animate-[surgindo_var(--crossfade)_ease-out_1]" : "opacity-0",
      )}
    />
  );
}
